import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

import { ReactComponent as EyeIcon } from '../../assets/icons/eye-icon.svg';
import { ReactComponent as FileUploadIcon } from '../../assets/icons/file-icon.svg';
import { ReactComponent as RemoveFileIcon } from '../../assets/icons/remove-icon.svg';
import { ReactComponent as DocumentIcon } from '../../assets/icons/tables/document-file.svg';
import { MultiMediaData } from '../../interface/account-management.interface';

interface CustomInputProperties {
  inputLabel?: string;
  acceptedFileType: string;
  placeholder: string;
  selectedFiles: File[] | null;
  name: string;
  onChangeValue: (name: string, file: File[]) => void;
  valueRequired?: boolean;
  readOnly?: boolean;
  unselectHandler?: (
    name: string,
    url: string,
    indexToRemove: number,
    type: 'newFile' | 'existingFile',
  ) => void;
  existingName?: string;
  existingFiles?: MultiMediaData[] | null;
  error?: boolean;
  errorMessage?: string;
  disabled?: boolean;
}

/**
 * File upload input box
 */
function CustomFileInput({
  inputLabel,
  acceptedFileType,
  name,
  placeholder,
  selectedFiles,
  onChangeValue,
  valueRequired,
  readOnly,
  unselectHandler,
  existingName,
  existingFiles,
  error,
  errorMessage,
  disabled,
}: CustomInputProperties) {
  const inputReference = useRef<HTMLInputElement | null>(null);
  const reference = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState<string>('close');
  const [style, setStyle] = useState<any>({});
  /**
   * Function to handle select file
   */
  const handleSelectFile = (selectedFileList: Array<File>) => {
    if (selectedFileList?.length) {
      onChangeValue(name, selectedFileList);
    }
  };

  /**
   * Function to handle file unselect
   * @param indexToRemove
   */
  const handleRemoveSelectedFile = (
    url: string,
    indexToRemove: number,
    type: 'newFile' | 'existingFile',
  ) => {
    if (unselectHandler && type === 'newFile') {
      unselectHandler(name, url, indexToRemove, type);
    } else if (unselectHandler && type === 'existingFile' && existingName) {
      unselectHandler(existingName, url, indexToRemove, type);
    } else {
      const afterRemovedSelectedFile =
        selectedFiles?.filter((_, index: number) => index !== indexToRemove) || [];
      onChangeValue(name, afterRemovedSelectedFile);
    }
  };

  /**
   * Handle the close th drop down function
   * @param event
   */
  const handleClickOutside = (event: MouseEvent) => {
    if (reference.current && !reference.current.contains(event.target as Node)) {
      setOpen('close');
    }
  };

  /**
   * Get the dropdown position
   */
  const openDropDown = (event: any) => {
    if (existingFiles?.length === 1) {
      window.open(existingFiles[0].signedUrl, '_block');
    } else {
      const rect = event.target.closest('.custom-file-input-container').getBoundingClientRect();
      setStyle({
        top: rect.top + rect.height,
        left: rect.left,
      });
      setOpen('open');
    }
  };

  useEffect((): void => {
    document.addEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="custom-file-input-wrap">
      {inputLabel && (
        <p className="input-label">
          {inputLabel}
          {valueRequired && <span>*</span>}
        </p>
      )}
      <div className={`custom-file-input-container ${error && 'error'}`}>
        <div className="selected-file-wrap">
          {existingFiles?.length
            ? existingFiles.map((existingFile, index) => {
                if (index >= 2) {
                  return null;
                }
                return (
                  <div className="single-selected-file">
                    <span className="doc-icon">
                      <DocumentIcon />
                    </span>
                    <p>{existingFile?.fileName}</p>
                    {!readOnly && (
                      <span
                        className="remove-icon"
                        role="presentation"
                        onClick={() =>
                          handleRemoveSelectedFile(existingFile.url, index, 'existingFile')
                        }>
                        <RemoveFileIcon />
                      </span>
                    )}
                  </div>
                );
              })
            : null}
          {existingFiles && existingFiles?.length > 2 ? (
            <div className="count-tag">+{existingFiles.length - 2}</div>
          ) : null}
          {(() => {
            let selectedFileView;
            if (selectedFiles?.length) {
              selectedFileView = selectedFiles.map((selectedFile, index) => (
                <div className="single-selected-file">
                  <span className="doc-icon">
                    <DocumentIcon />
                  </span>
                  <p>{selectedFile?.name}</p>
                  {!readOnly && (
                    <span
                      className="remove-icon"
                      role="presentation"
                      onClick={() => handleRemoveSelectedFile('', index, 'newFile')}>
                      <RemoveFileIcon />
                    </span>
                  )}
                </div>
              ));
            } else if (
              (selectedFiles?.length === 0 || !selectedFiles) &&
              (existingFiles?.length === 0 || !existingFiles)
            ) {
              selectedFileView = <p className="placeholder">{placeholder}</p>;
            }
            return selectedFileView;
          })()}
        </div>
        <input
          ref={inputReference}
          type="file"
          accept={acceptedFileType}
          multiple
          style={{ display: 'none' } as React.CSSProperties}
          onChange={(event) => {
            const files = event?.target?.files;
            if (files) {
              handleSelectFile(Array.from(files));
              // Reset input value to allow selecting the same file again
              /* eslint-disable no-param-reassign */
              event.target.value = '';
            }
          }}
        />
        <div className="file-input-icon-wrap">
          {readOnly ? (
            <span className="file-input-icon" role="presentation" onClick={openDropDown}>
              <EyeIcon />
            </span>
          ) : (
            <span
              className={`file-input-icon ${disabled && 'disabled'}`}
              role="presentation"
              onClick={() => {
                if (!readOnly && !disabled) {
                  inputReference.current?.click();
                }
              }}>
              <FileUploadIcon />
            </span>
          )}
        </div>
      </div>
      {error && <p className="text-danger">{errorMessage}</p>}
      {ReactDOM.createPortal(
        <div className={`dropdown-items-list ${open}`} ref={reference} style={style}>
          {existingFiles?.map((option) => (
            <div
              className="items"
              onClick={() => {
                window.open(option.signedUrl, '_block');
              }}
              role="presentation">
              {option?.fileName}
            </div>
          ))}
        </div>,
        document.body,
      )}
    </div>
  );
}

CustomFileInput.defaultProps = {
  inputLabel: '',
  valueRequired: false,
  readOnly: false,
  unselectHandler: null,
  existingName: '',
  existingFiles: [],
  error: false,
  errorMessage: '',
  disabled: false,
};

export default CustomFileInput;
